import React from 'react';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import ContactForm from '../../views/contacts/ContactForm';
import LocationForm from '../../views/contacts/LocationForm';
import PersonForm from '../../views/contacts/PersonForm';
import AssetForm from '../../views/assets/AssetForm';
import { useAuthContext } from 'services/authentication/AuthenticationContext';

// Rename the name of the function:
const ContractForm: React.FC<{
    viewKey?: string;
    linkedItem?: { [key: string]: any };
}> = ({
    viewKey,
    linkedItem,
}) => {
    const { userDivision } = useAuthContext();

    // Set the api object
    const apiObject = 'contract';

    // Configure the contract fields
    const fields: FieldType[] = [
        {
            type: 'searchselect',
            name: 'client',
            label: 'contract.general.client_label',
            placeholder: 'contract.general.client_placeholder',
            objectName: 'client.general.object_name.singular',
            query: {
                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                endpoint: 'get_contact_search',
                params: {
                    contact_type: ['contact', 'lead', 'client']
                }
            },
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
            },
            storeWholeObject: true,
            addNewItem: {
                method: 'modal-form',
                modalForm: <ContactForm />,
                linkedItem: { contact_type: 'client' },
                onSuccess: (response, { setUpdatedData }) => {
                    setUpdatedData(prev => {
                        let updated = { ...prev };

                        // Set the newly added person of the organization, as location person
                        if (response.entity_type === 'organization' && response.primary_person) {
                            if (!prev.location_person) {
                                updated.location_person = {
                                    id: response.primary_person.id,
                                    value: response.primary_person.id,
                                    name: response.primary_person.name,
                                    ...response.primary_person
                                };
                            }
                        }

                        // Reset the location person value, if the selected client is a person
                        if (response.entity_type === "person") {
                            updated.location_person = undefined;
                        }

                        // Set the newly added location, as work location
                        if (response.primary_locations && response.primary_locations.length > 0) {
                            if (!prev.work_location) {
                                const newLocation = response.primary_locations[0];
                                updated.work_location = {
                                    id: newLocation.id,
                                    value: newLocation.id,
                                    address: newLocation.address,
                                    ...newLocation
                                };
                            }
                        }

                        return updated;
                    });
                }
            },
        },
        {
            type: 'searchselect',
            name: 'work_location',
            label: 'contract.general.work_location_label',
            placeholder: 'contract.general.work_location_placeholder',
            objectName: 'location.general.object_name.singular',
            query: { endpoint: 'get_location_search' },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.client?.id) return null;
                return { endpoint: `get_location_search/${updatedData.client.id}` };
            },
            selectionFormat: 'address',
            optionFormat: { title: { field: 'address', format: (value) => value }},
            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> },
            directOptionsTitle: 'job.general.locations_of_client_label',
            storeWholeObject: true,
        },
        {
            type: 'multi-searchselect',
            name: 'assets',
            label: 'contract.general.assets_label',
            placeholder: 'contract.general.assets_placeholder',
            tooltipText: 'contract.general.assets_tooltip',
            objectName: 'asset.general.object_name.singular',
            activeFeature: 'asset_management',
            query: (updatedData) => {
                if (updatedData?.work_location?.id) {
                    return { endpoint: `get_asset_search/${updatedData.work_location.id}` };
                }
                return { endpoint: 'get_asset_search' };
            },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.work_location?.id) return null;
                return { endpoint: `get_asset_search/${updatedData.work_location.id}` };
            },
            directOptionsTitle: 'asset.general.assets_of_location_label',
            selectionFormat: 'name',
            optionFormat: {
                title: { field: 'name', format: (value) => value },
                subtitle: { field: 'number', format: (value) => value, optional: true },
            },
            specialOptions: [
                { value: 'all', label: 'asset.general.all_assets_option', dataField: 'all_assets' },
            ],
            addNewItem: {
                method: 'modal-form',
                modalForm: <AssetForm />,
                linkedItem: (updatedData) => updatedData?.work_location?.id ? { location: updatedData.work_location.id } : undefined,
            },
        },
        {
            type: 'searchselect',
            name: 'location_person',
            label: 'contract.general.location_person_label',
            placeholder: 'contract.general.location_person_placeholder',
            objectName: 'person.general.object_name.singular',
            query: { endpoint: 'get_person_search' },
            showOptionsOnClick: (updatedData) => {
                if (!updatedData.client?.id) return null;
                return { endpoint: `get_person_search/${updatedData.client.id}` };
            },
            selectionFormat: 'name',
            optionFormat: { title: { field: 'name', format: (value) => value } },
            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> },
            dependentOn: [
                { fieldName: 'client.entity_type', notValue: 'person' },
            ],
            directOptionsTitle: 'job.general.persons_of_client_label'
        },
        {
            type: 'header',
        },
        {
            type: 'text',
            name: 'reference',
            label: 'contract.general.reference_label',
            placeholder: 'contract.general.reference_placeholder',
        },
        {
            type: 'date',
            name: 'start_date',
            label: 'contract.general.start_date_label',
            width: '50%',
            group: 'field-row',
        },
        {
            type: 'date',
            name: 'end_date',
            label: 'contract.general.end_date_label',
            helperText: 'contract.general.end_date_helper',
            width: '50%',
            group: 'field-row',
        },
        {
            type: 'dropdown',
            name: 'division',
            label: 'contract.general.division_label',
            disabled_selected: 'contract.general.division_disabled_selected',
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true },
                useCache: true
            }],
            allowNoneOption: false,
            showSearch: false,
            activeFeature: 'divisions',
            ...(userDivision ? { defaultValue: { value: String(userDivision) } } : {}),
        },
    ];

    return (
        <FormModal
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            linkedItem={linkedItem}
            redirectTo={{ url: 'contracts', itemIdField: 'id' }}
        />
    );
};

export default ContractForm;
